// Guide content for all pages
export const guideContent = {
  dashboard: {
    pageName: "Dashboard",
    steps: [
      {
        title: "Welcome to Lead Generation App",
        description: "This is your main dashboard where you can see an overview of your lead generation activities and access all features.",
      },
      {
        title: "Quick Stats Overview",
        description: "View your API requests remaining, places found, active leads, search areas, and social links discovered.",
        tip: "Monitor your API usage to avoid running out of requests during important searches."
      },
      {
        title: "Main Actions Section",
        description: "Do a manual search or manage your saved data.",
      },
      {
        title: "Getting Started",
        description: "Click 'Try Manual Search' to begin finding potential leads in your target areas."
      }
    ]
  },

  search: {
    pageName: "Manual Search",
    steps: [
      {
        title: "Search Mode Selection",
        description: "Choose between Circles (simple), Pentagon (custom area), or Tube (donut shape) search modes.",
        tip: "Start with Circles mode for simple searches, then try Pentagon for custom boundaries."
      },
      {
        title: "Set Your Search Radius",
        description: "Adjust the radius slider or enter a custom value (50-50,000 meters) for new search areas.",
        tip: "1000-2000m radius works well for dense urban areas, 3000-5000m for suburban areas."
      },
      {
        title: "Click on Map to Add Areas",
        description: "Click anywhere on the map to create search circles. Use city navigation buttons for quick location jumps.",
        tip: "Add multiple adjacent areas for comprehensive coverage of your target region."
      },
      {
        title: "Configure Business Types",
        description: "Add business types to include (what you want to find) and exclude (what you want to avoid).",
        tip: "Be specific with business types - 'restaurant' finds all restaurants, 'italian_restaurant' is more targeted."
      },
      {
        title: "Advanced Shape Drawing",
        description: "For Pentagon/Tube modes: Click map to draw shapes, press 'M' to toggle Move/Reshape modes, use Ctrl+Z to undo.",
        tip: "Tube shapes are perfect for highway corridors or areas around specific landmarks."
      },
      {
        title: "Execute Your Search",
        description: "Click 'Search' to find businesses. Results appear below with options to remove unwanted places before saving.",
        tip: "Review results carefully and remove chain businesses or irrelevant places before saving."
      }
    ]
  },

  intelligentSearch: {
  pageName: "Intelligent Search",
  steps: [
    {
      title: "Step 1: Choose Your Location Method",
      description: "You can either search for a specific location (city, region, etc.) using the search bar, OR draw a custom boundary (Pentagon/Tube) directly on the map.",
      tip: "For beginners, searching for a city name is the easiest way to get started with AI optimization."
    },
    {
      title: "Location Search Method",
      description: "Type a city, borough, region, or country name in the search bar. The system will automatically find the location and create a boundary around it.",
      tip: "Try searching for specific areas like 'Manhattan, New York' or 'Camden, London' for more targeted results."
    },
    {
      title: "Custom Boundary Method (Alternative)",
      description: "Alternatively, you can draw custom Pentagon or Tube shapes by selecting the mode and clicking on the map to create your own search boundaries.",
      tip: "Custom boundaries are useful for specific corridors, business districts, or unique geographic areas."
    },
    {
      title: "Step 2: Configure Your Search Parameters",
      description: "After selecting your location, configure the business types to include/exclude and enable AI optimization for intelligent circle placement.",
      tip: "AI optimization uses population density data to place circles in the most promising business areas."
    },
    {
      title: "Step 3: Generate Optimized Circles",
      description: "Click 'Create Search Area & Generate Circles' to let the AI automatically create optimized search circles within your selected boundary.",
      tip: "The system analyzes demographic data to place circles where businesses are most likely to be found."
    },
    {
      title: "Step 4: Review and Select Circles",
      description: "Review the AI-generated circles and select which ones to search. You can select all or choose specific high-confidence circles.",
      tip: "Higher confidence scores indicate areas more likely to contain your target business types."
    },
    {
      title: "Step 5: Execute Search and Save Results",
      description: "Run the search on selected circles, review the results, and save promising places to your collection for further analysis.",
      tip: "The chain detection feature helps identify and filter franchise businesses if you prefer independent businesses."
    }
  ]
},

  places: {
    pageName: "Saved Places",
    steps: [
      {
        title: "Speed Vetting Interface",
        description: "This is your lead qualification hub. Each place shows key indicators: rating, status, contact info, and social links.",
        tip: "Look for high ratings (4.0+), operational status, and available contact information for best leads."
      },
      {
        title: "Quick Filters",
        description: "Use filters to find specific types of places: by lead status, social links, websites, or search terms.",
        tip: "Filter by 'No Social Links' to find places that need social media discovery."
      },
      {
        title: "Social Media Discovery",
        description: "Click on places with social link counts to expand and view discovered social profiles and contact info.",
        tip: "Verified links (with checkmarks) are more reliable for outreach efforts."
      },
      {
        title: "Crawl for Social Media",
        description: "Use the crawl button to discover social media profiles and contact information for places.",
        tip: "Batch crawling is more efficient - select multiple places and use 'Crawl Selected'."
      },
      {
        title: "Convert to Leads",
        description: "Select promising places and click 'Convert to Leads' to move them to your active outreach pipeline.",
        tip: "Convert places with good ratings, contact info, and clear business needs."
      },
      {
        title: "Batch Operations",
        description: "Select multiple places for bulk actions: convert to leads, delete, or start crawling operations.",
        tip: "Use 'Select All' checkbox for bulk operations on the entire page."
      }
    ]
  },

  leads: {
    pageName: "Leads Management",
    steps: [
      {
        title: "Lead Pipeline Overview",
        description: "Manage your outreach pipeline with leads organized by contact status and priority levels.",
        tip: "Focus on 'Not Contacted' leads first, then follow up on 'Contacted' leads."
      },
      {
        title: "Lead Status Tracking",
        description: "Track progress from initial contact through conversion using status indicators and priority levels.",
        tip: "Update status after each interaction to maintain accurate pipeline tracking."
      },
      {
        title: "Contact Information Access",
        description: "Expand leads to view social media links, contact details, and communication history.",
        tip: "Use multiple contact methods - email, phone, and social media for best response rates."
      },
      {
        title: "Response Management",
        description: "Record responses, set follow-up dates, and track communication outcomes for each lead.",
        tip: "Document all interactions in the notes section for future reference."
      },
      {
        title: "Lead Editing",
        description: "Click 'Edit' to update lead information, add notes, set priorities, and track outreach progress.",
        tip: "Regular updates help identify the most effective outreach strategies."
      }
    ]
  },

  crawling: {
    pageName: "Web Crawling",
    steps: [
      {
        title: "Crawling Dashboard",
        description: "Monitor your social media discovery operations with real-time statistics and recent activity.",
        tip: "Check success rates to identify any issues with specific websites or platforms."
      },
      {
        title: "Batch Crawling Setup",
        description: "Select multiple places from your saved collection to crawl for social media information simultaneously.",
        tip: "Crawling works best on places with websites - prioritize those first."
      },
      {
        title: "Monitor Active Tasks",
        description: "Track ongoing crawl operations and view discovered social media links in real-time.",
        tip: "Crawling typically takes 10-20 seconds per place, depending on website complexity."
      },
      {
        title: "Review Discovered Links",
        description: "Examine found social media profiles, contact information, and verification status for quality.",
        tip: "Focus on verified links and those with high confidence scores for outreach."
      },
      {
        title: "Social Media Analysis",
        description: "View platform-specific breakdowns and success rates to optimize your crawling strategy.",
        tip: "Some platforms are more reliable than others - adjust strategy based on success rates."
      }
    ]
  }
};

// Helper function to get guide content for a specific page
export const getGuideContent = (pageName) => {
  return guideContent[pageName] || null;
};